import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { COLOR_THEMES } from './constants';

/**
 * Reusable accordion component for collapsible sections
 * 
 * @param {Object} props
 * @param {string} props.title - Section title
 * @param {React.ReactNode} props.children - Content to show/hide
 * @param {boolean} props.defaultExpanded - Whether section is expanded by default
 * @param {string} props.previewText - Text to show when collapsed (optional)
 * @param {React.ReactNode} props.previewComponent - React component to show when collapsed (optional)
 * @param {string} props.colorTheme - Optional color theme ('blue', 'amber', 'red', 'green', 'gray')
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.expanded - Controlled expanded state (optional)
 * @param {Function} props.onToggle - Callback when toggled (optional)
 */
const AccordionSection = ({
  title,
  children,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onToggle,
  previewText,
  previewComponent,
  colorTheme = 'gray',
  className = '',
}) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  
  // Use controlled state if provided, otherwise use internal state
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  const toggle = () => {
    if (onToggle) {
      onToggle(!isExpanded);
    } else {
      setInternalExpanded(!isExpanded);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  const theme = COLOR_THEMES[colorTheme] || COLOR_THEMES.gray;

  return (
    <div className={`rounded-lg border ${theme.border} ${className}`}>
      <button
        onClick={toggle}
        onKeyDown={handleKeyDown}
        className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${theme.headerHover} rounded-t-lg select-text ${
          !isExpanded ? 'rounded-b-lg' : ''
        }`}
        aria-expanded={isExpanded}
        aria-controls={`accordion-content-${title.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className="flex items-center gap-2 select-text">
          <span className="text-xs uppercase tracking-wide text-gray-400 font-medium select-text">
            {title}
          </span>
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
            isExpanded ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      
      {isExpanded ? (
        <div
          id={`accordion-content-${title.replace(/\s+/g, '-').toLowerCase()}`}
          className={`px-4 py-4 ${theme.bg} ${theme.text} rounded-b-lg`}
        >
          {children}
        </div>
      ) : previewComponent ? (
        <div
          className={`px-4 py-3 ${theme.bg} ${theme.text} rounded-b-lg`}
        >
          {previewComponent}
        </div>
      ) : previewText ? (
        <div
          className={`px-4 py-3 ${theme.bg} ${theme.text} rounded-b-lg text-sm opacity-75`}
        >
          <p className="truncate">{previewText}</p>
        </div>
      ) : null}
    </div>
  );
};

AccordionSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  defaultExpanded: PropTypes.bool,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
  previewText: PropTypes.string,
  previewComponent: PropTypes.node,
  colorTheme: PropTypes.oneOf(['blue', 'amber', 'red', 'green', 'gray']),
  className: PropTypes.string,
};

export default AccordionSection;

